import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { CircularProgress } from "@mui/material";

import Box from "@/main/components/shared/box";
import { getTranslateByKey } from "@/main/services/utils";
import { KogTabsComponent } from "@/main/components/shared/tabs/tabs";
import {
	TemplateEmailNotificationDetailVariableResponse,
	TemplateEmailNotificationVariableResponse,
} from "@/main/models/response/templateEmailNotificationResponse";

import { Editor } from "@tiptap/core";

import Toolbar from "./toolbar";
import {
	TemplateVariable,
	DefaultTemplateVariable,
	htmlToEditorContent,
	editorContentToHtml,
} from "./extensions/templateVariable";
import { FontSize } from "./extensions/fontSizeExtension";
import { TextColor } from "./extensions/textColorExtension";
import { CustomParagraph } from "./extensions/paragraphExtension";
import { CustomTable, CustomTableRow, CustomTableCell, CustomTableHeader } from "./extensions/tableExtensions";
import VariableTemplateEmail from "./variableTemplateEmail/variableTemplateEmail";
import { validateVariablePaste, buildVariableLineNodes } from "./variablePaste";
import SubjectEditor from "./subjectEditor/subjectEditor";
import * as S from "./styles";

export interface ICultureData {
	culture: string;
	cultureName: string;
	title: string;
	body: string;
	templateEmailNotificationCompanyGuid: string;
	primaryColor?: string;
}

interface ICultureTabContentProps {
	data: ICultureData;
	isVisible: boolean;
	defaultVariables: string[];
	availableVariables: TemplateEmailNotificationVariableResponse[];
	onTitleChange: (title: string) => void;
	onBodyChange: (body: string) => void;
}

function CultureTabContent({
	data,
	isVisible,
	defaultVariables,
	availableVariables,
	onTitleChange,
	onBodyChange,
}: ICultureTabContentProps) {
	const availableVariablesRef = useRef(availableVariables);
	useEffect(() => {
		availableVariablesRef.current = availableVariables;
	}, [availableVariables]);

	const editor = useEditor({
		extensions: [
			StarterKit.configure({ paragraph: false }),
			CustomParagraph,
			Underline,
			TextStyle,
			FontSize,
			TextColor,
			CustomTable.configure({ resizable: false }),
			CustomTableRow,
			CustomTableHeader,
			CustomTableCell,
			TemplateVariable,
			DefaultTemplateVariable,
		],
		content: htmlToEditorContent(data.body, defaultVariables, data.primaryColor),
		editorProps: {
			handleTextInput(_view, _from, _to, text) {
				return text.includes("{") || text.includes("}");
			},
			handlePaste(view, event) {
				const text = event.clipboardData?.getData("text/plain") ?? "";

				const result = validateVariablePaste(text, availableVariablesRef.current, false, data.cultureName);
				if (result.status === "passthrough") return false;
				if (result.status === "invalid") return true;

				const { schema, tr, selection } = view.state;
				const hardBreakType = schema.nodes.hardBreak;
				const nodes: ReturnType<typeof schema.text>[] = [];

				const lines = text.split("\n");
				lines.forEach((line, lineIdx) => {
					if (lineIdx > 0 && hardBreakType) nodes.push(hardBreakType.create());
					nodes.push(...buildVariableLineNodes(line, schema));
				});

				if (nodes.length === 0) return true;

				view.dispatch(tr.replaceWith(selection.from, selection.to, nodes).scrollIntoView());
				return true;
			},
		},
		onUpdate: ({ editor: ed }: { editor: Editor }) => {
			onBodyChange(editorContentToHtml(ed.getHTML(), data.primaryColor));
		},
	});

	const cultureLabel = data.cultureName;

	return (
		<S.CultureTabPanel $visible={isVisible}>
			<S.FieldGroup>
				<S.FieldLabel>
					{getTranslateByKey("information.title")} ({cultureLabel}) *
				</S.FieldLabel>
				<SubjectEditor
					id={`id-title-input-${data.culture}`}
					value={data.title}
					variables={availableVariables}
					onChange={onTitleChange}
					cultureName={data.cultureName}
				/>
			</S.FieldGroup>

			<S.FieldGroup>
				<S.FieldLabel>
					{getTranslateByKey("information.body")} ({cultureLabel}) *
				</S.FieldLabel>
				<S.EditorWrapper>
					<Toolbar editor={editor} primaryColor={data.primaryColor} />
					<S.EditorContent>
						<EditorContent editor={editor} />
					</S.EditorContent>
				</S.EditorWrapper>
			</S.FieldGroup>
		</S.CultureTabPanel>
	);
}

export interface ISectionEmailTemplateBodyHandle {
	triggerSave: () => Promise<void>;
	getIsSaving: () => boolean;
}

interface ISectionEmailTemplateBodyProps {
	cultures?: ICultureData[];
	variables?: TemplateEmailNotificationVariableResponse[];
	variablesDefault?: TemplateEmailNotificationDetailVariableResponse[];
	isLoading?: boolean;
	onSave?: (cultures: ICultureData[]) => Promise<void>;
}

const SectionEmailTemplateBody = forwardRef<ISectionEmailTemplateBodyHandle, ISectionEmailTemplateBodyProps>(
	function SectionEmailTemplateBody(
		{
			cultures = [],
			variables: variablesProp = [],
			variablesDefault = [],
			isLoading = false,
			onSave,
		}: ISectionEmailTemplateBodyProps,
		ref,
	) {
		const [isSaving, setIsSaving] = useState(false);
		const [activeTabIndex, setActiveTabIndex] = useState(0);
		const [localData, setLocalData] = useState<ICultureData[]>(cultures);
		const [dirtySet, setDirtySet] = useState<Set<string>>(new Set());

		useImperativeHandle(ref, () => ({
			triggerSave: handleSave,
			getIsSaving: () => isSaving,
		}));
		const [variables, setVariables] = useState<TemplateEmailNotificationVariableResponse[]>(variablesProp);

		useEffect(() => {
			setVariables(variablesProp);
		}, [variablesProp]);

		useEffect(() => {
			setLocalData(cultures);
		}, [cultures]);

		const defaultVariableNames = variablesDefault
			.filter((v) => v.isDefault)
			.map((v) => v.variable.replace(/^\{\{|\}\}$/g, "").trim());

		const activeCulture = localData[activeTabIndex];

		function handleTitleChange(culture: string, title: string) {
			setLocalData((prev) => prev.map((c) => (c.culture === culture ? { ...c, title } : c)));
			setDirtySet((prev) => new Set(prev).add(culture));
		}

		function handleBodyChange(culture: string, body: string) {
			setLocalData((prev) => prev.map((c) => (c.culture === culture ? { ...c, body } : c)));
			setDirtySet((prev) => new Set(prev).add(culture));
		}

		async function handleSave() {
			if (onSave) {
				setIsSaving(true);
				try {
					const dirtyData = localData.filter((c) => dirtySet.has(c.culture));
					await onSave(dirtyData);
					setDirtySet(new Set());
				} finally {
					setIsSaving(false);
				}
				return;
			}
		}

		return (
			<Box>
				<S.PageLayout>
					<S.EditorColumn>
						<>
							{isLoading ? (
								<S.LoadingWrapper>
									<CircularProgress size={32} />
								</S.LoadingWrapper>
							) : (
								<KogTabsComponent
									tabCurrent={activeTabIndex}
									onChangeTab={setActiveTabIndex}
									tabPanels={localData.map((c) => ({
										tab: c.cultureName + (dirtySet.has(c.culture) ? " *" : ""),
										tabLabel: dirtySet.has(c.culture) ? (
											<span>
												{c.cultureName}
												<span style={{ color: "#d32f2f", marginLeft: "2px" }}>*</span>
											</span>
										) : (
											c.cultureName
										),
										isShowControl: true,
										panel: (
											<CultureTabContent
												key={c.culture}
												data={c}
												isVisible={true}
												defaultVariables={defaultVariableNames}
												availableVariables={variables}
												onTitleChange={(title) => handleTitleChange(c.culture, title)}
												onBodyChange={(body) => handleBodyChange(c.culture, body)}
											/>
										),
									}))}
								/>
							)}
						</>
					</S.EditorColumn>

					<S.VariableColumn>
						<VariableTemplateEmail
							variables={variables}
							activeTitleContent={activeCulture?.title ?? ""}
							activeBodyContent={activeCulture?.body ?? ""}
						/>
					</S.VariableColumn>
				</S.PageLayout>
			</Box>
		);
	},
);

export default SectionEmailTemplateBody;
